import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, Sparkles, Sun, Layers, HelpCircle, RotateCcw, Plus, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="list-disc pl-5 mb-2">{listItems}</ul>);
      listItems = [];
    }
  };
  lines.forEach((line, i) => {
    if (line.startsWith("## ")) { flushList(); elements.push(<h2 key={i}>{line.slice(3)}</h2>); }
    else if (line.startsWith("### ")) { flushList(); elements.push(<h3 key={i}>{line.slice(4)}</h3>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { listItems.push(<li key={i}>{fmtInline(line.slice(2))}</li>); }
    else if (line.match(/^\d+\.\s/)) { listItems.push(<li key={i}>{fmtInline(line.replace(/^\d+\.\s/, ""))}</li>); }
    else if (line.trim() === "") { flushList(); elements.push(<div key={i} className="h-2" />); }
    else { flushList(); elements.push(<p key={i}>{fmtInline(line)}</p>); }
  });
  flushList();
  return <div className="markdown-content">{elements}</div>;
}
function fmtInline(text) {
  return text.split(/(\*\*.*?\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  );
}

export default function TarotPage() {
  const { language } = useLang();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("select"); // select | question | drawing | reading
  const [spreadType, setSpreadType] = useState(null);
  const [question, setQuestion] = useState("");
  const [cards, setCards] = useState([]); // main drawn cards
  const [clarifiers, setClarifiers] = useState({}); // { positionIndex: [card, ...] }
  const [flipped, setFlipped] = useState({}); // { index: true }
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interpLoading, setInterpLoading] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeTimeLeft, setUpgradeTimeLeft] = useState("");
  const endRef = useRef(null);

  const checkReadingLimit = async () => {
    try {
      const res = await axios.post(`${API}/user/use-reading`);
      if (res.data.success) return true;
      setUpgradeTimeLeft(res.data.next_free_in || "24h");
      setShowUpgradePrompt(true);
      return false;
    } catch { return true; }
  };

  const scroll = useCallback(() => {
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
  }, []);

  const allDrawnIds = () => {
    const ids = cards.map(c => c.id);
    Object.values(clarifiers).forEach(arr => arr.forEach(c => ids.push(c.id)));
    return ids;
  };

  const drawCards = async (count, type) => {
    const allowed = await checkReadingLimit();
    if (!allowed) return;
    setLoading(true);
    setSpreadType(type);
    setCards([]);
    setClarifiers({});
    setFlipped({});
    setInterpretation(null);
    setChatMessages([]);
    setPhase("drawing");
    try {
      const res = await axios.post(`${API}/tarot/draw`, { count, exclude_ids: [] });
      if (res.data.success) {
        setCards(res.data.cards);
        // Flip cards one by one with delay
        for (let i = 0; i < res.data.cards.length; i++) {
          await new Promise(r => setTimeout(r, 600));
          setFlipped(prev => ({ ...prev, [i]: true }));
        }
        setPhase("reading");
        // Auto-interpret
        await interpret(res.data.cards, type, question, {});
      }
    } catch {
      setPhase("select");
    } finally {
      setLoading(false);
    }
  };

  const drawCardOfDay = async () => {
    const allowed = await checkReadingLimit();
    if (!allowed) return;
    setLoading(true);
    setSpreadType("card_of_day");
    setCards([]);
    setClarifiers({});
    setFlipped({});
    setInterpretation(null);
    setChatMessages([]);
    setPhase("drawing");
    try {
      const res = await axios.get(`${API}/tarot/card-of-day`);
      if (res.data.success) {
        setCards([res.data.card]);
        await new Promise(r => setTimeout(r, 600));
        setFlipped({ 0: true });
        setPhase("reading");
        await interpret([res.data.card], "card_of_day", "", {});
      }
    } catch {
      setPhase("select");
    } finally {
      setLoading(false);
    }
  };

  const interpret = async (drawnCards, type, q, clar) => {
    setInterpLoading(true);
    try {
      const res = await axios.post(`${API}/tarot/interpret`, {
        cards: drawnCards, spread_type: type, question: q, clarifiers: clar, language,
      }, { timeout: 120000 });
      if (res.data.success) {
        setInterpretation(res.data.interpretation);
      }
    } catch {
      setInterpretation("The spirits are quiet right now. Please try again.");
    } finally {
      setInterpLoading(false);
    }
  };

  const drawClarifier = async (posIndex) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tarot/draw`, { count: 1, exclude_ids: allDrawnIds() });
      if (res.data.success && res.data.cards.length > 0) {
        const newCard = res.data.cards[0];
        const updated = { ...clarifiers, [posIndex]: [...(clarifiers[posIndex] || []), newCard] };
        setClarifiers(updated);
        // Re-interpret with clarifiers
        await interpret(cards, spreadType, question, updated);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = () => {
    if (!question.trim()) return;
    drawCards(3, "three_card");
  };

  const handleFollowUp = async () => {
    const text = followUp.trim();
    if (!text || interpLoading) return;
    setFollowUp("");
    setChatMessages(prev => [...prev, { sender: "user", text }]);
    setInterpLoading(true);
    try {
      const res = await axios.post(`${API}/tarot/interpret`, {
        cards, spread_type: spreadType, question: text,
        clarifiers, language,
      }, { timeout: 120000 });
      if (res.data.success) {
        setChatMessages(prev => [...prev, { sender: "bot", text: res.data.interpretation }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { sender: "bot", text: "The spirits are quiet. Try again." }]);
    } finally {
      setInterpLoading(false);
    }
  };

  const reset = () => {
    setPhase("select");
    setSpreadType(null);
    setQuestion("");
    setCards([]);
    setClarifiers({});
    setFlipped({});
    setInterpretation(null);
    setChatMessages([]);
    setLoading(false);
    setInterpLoading(false);
  };

  const SPREAD_LABELS = { three_card: ["Past", "Present", "Future"], single: ["Your Card"], card_of_day: ["Card of the Day"] };

  // Upgrade prompt — rendered above all screens so it always shows
  const upgradeModal = showUpgradePrompt ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="glass rounded-2xl p-6 max-w-sm w-full border border-violet-500/30 text-center">
        <Sparkles className="h-8 w-8 text-violet-400 mx-auto mb-3" />
        <h3 className="font-cinzel text-lg text-violet-400 font-bold mb-2">Free Readings Used</h3>
        <p className="text-sm text-slate-300 mb-1">Next free reading in:</p>
        <p className="text-2xl font-bold text-violet-400 my-3">{upgradeTimeLeft}</p>
        <p className="text-xs text-slate-500 mb-5">Purchase more readings to continue now.</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowUpgradePrompt(false)} className="flex-1 border-slate-500/30 text-slate-400 text-xs">I'll wait</Button>
          <Button onClick={() => { setShowUpgradePrompt(false); navigate("/upgrade"); }} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-semibold text-xs">Get More Readings</Button>
        </div>
      </div>
    </div>
  ) : null;

  // --- SELECT SCREEN ---
  if (phase === "select") {
    return (
      <div className="flex flex-col items-center justify-center min-h-app-screen px-4 py-8" data-testid="tarot-select">
        {upgradeModal}
        <Sparkles className="h-10 w-10 text-violet-400 mb-4" />
        <h1 className="font-cinzel text-3xl sm:text-4xl font-bold text-violet-400 tracking-wider mb-2 text-center">Tarot Reading</h1>
        <p className="text-slate-400 text-sm mb-6 sm:mb-10 text-center max-w-md">Rider-Waite Tarot — Draw cards and receive mystical guidance</p>
        <div className="grid gap-4 sm:grid-cols-3 max-w-2xl w-full">
          <button onClick={drawCardOfDay} disabled={loading}
            className="glass rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group border border-violet-500/20"
            data-testid="tarot-card-of-day">
            <Sun className="h-8 w-8 text-amber-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-amber-400 font-bold text-sm tracking-wide mb-1">Card of the Day</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">Draw your daily guidance card</p>
          </button>
          <button onClick={() => drawCards(3, "three_card")} disabled={loading}
            className="glass rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group border border-violet-500/20"
            data-testid="tarot-three-card">
            <Layers className="h-8 w-8 text-violet-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-violet-400 font-bold text-sm tracking-wide mb-1">3-Card Spread</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">Past, Present & Future</p>
          </button>
          <button onClick={() => setPhase("question")} disabled={loading}
            className="glass rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group border border-violet-500/20"
            data-testid="tarot-ask-question">
            <HelpCircle className="h-8 w-8 text-cyan-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-cyan-400 font-bold text-sm tracking-wide mb-1">Ask a Question</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">Get answers through the cards</p>
          </button>
        </div>
      </div>
    );
  }

  // --- QUESTION INPUT ---
  if (phase === "question") {
    return (
      <div className="flex flex-col items-center justify-center min-h-app-screen px-4 py-8" data-testid="tarot-question">
        {upgradeModal}
        <HelpCircle className="h-10 w-10 text-cyan-400 mb-4" />
        <h2 className="font-cinzel text-2xl font-bold text-violet-400 tracking-wider mb-2 text-center">Ask the Cards</h2>
        <p className="text-slate-400 text-sm mb-8 text-center max-w-md">Focus on your question. The cards will reveal what you need to know.</p>
        <div className="w-full max-w-md space-y-4">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAskQuestion()}
            placeholder="What do you seek guidance on?"
            className="bg-white/5 border-violet-500/25 text-slate-100 placeholder:text-slate-500 h-12 focus-visible:ring-violet-500/50"
            data-testid="tarot-question-input" />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setPhase("select")} className="flex-1 border-slate-500/30 text-slate-400">Back</Button>
            <Button onClick={handleAskQuestion} disabled={!question.trim() || loading}
              className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-semibold gap-2" data-testid="tarot-ask-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Draw Cards
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- DRAWING / READING SCREEN ---
  const labels = SPREAD_LABELS[spreadType] || cards.map((_, i) => `Card ${i + 1}`);

  return (
    <div className="flex flex-col h-app-screen" data-testid="tarot-reading">
      {upgradeModal}
      <ScrollArea className="flex-1 px-4">
        <div className="max-w-3xl mx-auto py-6 space-y-6">
          {/* Question display */}
          {question && (
            <div className="text-center">
              <p className="text-xs text-slate-500 mb-1">Your question</p>
              <p className="text-sm text-violet-300 italic">"{question}"</p>
            </div>
          )}

          {/* Card spread */}
          <div className={`flex justify-center gap-4 sm:gap-6 flex-wrap`} data-testid="card-spread">
            {cards.map((card, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2">
                <p className="text-[11px] text-slate-500 font-cinzel tracking-wider">{labels[idx] || `Card ${idx + 1}`}</p>
                <div className="relative">
                  <TarotCard card={card} isFlipped={!!flipped[idx]} />
                  {/* Clarifier cards stacked on top */}
                  {(clarifiers[idx] || []).map((ccard, ci) => (
                    <div key={ci} className="absolute" style={{ top: -8 * (ci + 1), left: 6 * (ci + 1), zIndex: ci + 2 }}>
                      <TarotCard card={ccard} isFlipped={true} isSmall />
                    </div>
                  ))}
                </div>
                {/* Clarifier button */}
                {flipped[idx] && phase === "reading" && !loading && (
                  <button onClick={() => drawClarifier(idx)} disabled={interpLoading}
                    className="flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 transition-colors disabled:opacity-40"
                    data-testid={`clarifier-btn-${idx}`}>
                    <Plus className="h-3 w-3" /> Clarify
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Loading indicator */}
          {(loading && phase === "drawing") && (
            <div className="flex justify-center">
              <div className="flex items-center gap-2 text-violet-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Drawing from the deck...</span>
              </div>
            </div>
          )}

          {/* Interpretation */}
          {interpLoading && !interpretation && (
            <div className="glass rounded-2xl p-6 border border-violet-500/20">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="h-5 w-5 text-violet-400 animate-spin shrink-0" />
                <span className="text-sm text-slate-300 font-medium">Reading the cards...</span>
              </div>
              <div className="space-y-1.5 ml-8">
                <p className="text-xs text-slate-500">Interpreting card imagery, positions, and connections</p>
                <div className="flex items-center gap-2">
                  <div className="h-1 flex-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500/40 rounded-full animate-pulse" style={{width: "50%"}} />
                  </div>
                  <span className="text-[10px] text-slate-600">~20-30 sec</span>
                </div>
              </div>
            </div>
          )}
          {interpretation && (
            <div className="glass rounded-2xl p-5 border border-violet-500/20" data-testid="tarot-interpretation">
              {renderMarkdown(interpretation)}
            </div>
          )}

          {/* Follow-up chat messages */}
          {chatMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
              {msg.sender === "user" ? (
                <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[75%]">
                  <p className="text-sm text-white">{msg.text}</p>
                </div>
              ) : (
                <div className="glass rounded-2xl rounded-tl-sm px-5 py-4 max-w-[90%] border border-violet-500/10">
                  {renderMarkdown(msg.text)}
                </div>
              )}
            </div>
          ))}

          {interpLoading && chatMessages.length > 0 && (
            <div className="flex justify-start">
              <div className="glass rounded-2xl px-5 py-4 flex items-center gap-2 border border-violet-500/10">
                <Loader2 className="h-4 w-4 text-violet-400 animate-spin" />
                <span className="text-sm text-slate-400">Reading the cards...</span>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Bottom bar */}
      {phase === "reading" && (
        <div className="border-t-2 border-violet-500/30 bg-slate-950/95 backdrop-blur-lg p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.4)]" data-testid="tarot-chat-bar">
          <div className="max-w-3xl mx-auto space-y-3">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset}
                className="text-xs gap-1.5 border-slate-500/30 text-slate-400 hover:bg-white/5" data-testid="tarot-new-reading">
                <RotateCcw className="h-3.5 w-3.5" /> New Reading
              </Button>
            </div>
            <div className="flex gap-3 items-center">
              <Input value={followUp} onChange={(e) => setFollowUp(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFollowUp()}
                placeholder="Ask a follow-up about your reading..."
                disabled={interpLoading}
                className="flex-1 bg-white/5 border-violet-500/25 text-slate-100 placeholder:text-slate-400 h-12 text-sm focus-visible:ring-violet-500/50"
                data-testid="tarot-followup-input" />
              <Button onClick={handleFollowUp} disabled={interpLoading || !followUp.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white h-12 px-6 shrink-0 font-bold" data-testid="tarot-send-btn">
                {interpLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// --- TAROT CARD COMPONENT ---
function TarotCard({ card, isFlipped, isSmall }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const w = isSmall ? "w-28" : "w-32 sm:w-36";
  const h = isSmall ? "h-44" : "h-48 sm:h-56";

  return (
    <div className={`${w} ${h} [perspective:1000px]`} data-testid={`tarot-card-${card.id}`}>
      <div className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] ${isFlipped ? "[transform:rotateY(180deg)]" : ""}`}>
        {/* Card Back */}
        <div className="absolute inset-0 [backface-visibility:hidden] rounded-xl border-2 border-violet-500/40 overflow-hidden shadow-lg">
          <div className="w-full h-full bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950 flex items-center justify-center">
            <div className="w-16 h-16 border-2 border-violet-400/30 rounded-full flex items-center justify-center rotate-45">
              <div className="w-10 h-10 border border-violet-400/20 rounded-full flex items-center justify-center -rotate-45">
                <Sparkles className="h-5 w-5 text-violet-400/50" />
              </div>
            </div>
          </div>
        </div>

        {/* Card Front — Actual Rider-Waite image */}
        <div className={`absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-xl border-2 border-amber-600/50 overflow-hidden shadow-lg ${card.is_reversed ? "[transform:rotateY(180deg)_rotateZ(180deg)]" : ""}`}>
          <div className="w-full h-full bg-slate-900 flex flex-col">
            {/* Card image */}
            <div className="flex-1 relative overflow-hidden bg-amber-50">
              {card.image && !imgError ? (
                <>
                  <img src={card.image} alt={card.name}
                    className={`w-full h-full object-cover object-top ${imgLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
                    onLoad={() => setImgLoaded(true)}
                    onError={() => setImgError(true)}
                    loading="eager" />
                  {!imgLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-violet-900 to-indigo-950">
                      <Loader2 className="h-5 w-5 text-violet-400 animate-spin" />
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-900 to-indigo-950 p-2">
                  <p className={`font-cinzel font-bold text-center text-white/90 ${isSmall ? "text-[9px]" : "text-[11px]"}`}>{card.name}</p>
                </div>
              )}
            </div>
            {/* Card name strip */}
            <div className="bg-slate-950 px-1.5 py-1 text-center border-t border-amber-600/30">
              <p className={`font-cinzel font-bold text-amber-400 truncate ${isSmall ? "text-[7px]" : "text-[9px]"}`}>
                {card.name}
              </p>
              {card.is_reversed && (
                <span className={`inline-block font-bold text-red-400 bg-red-500/15 px-1 rounded ${isSmall ? "text-[6px]" : "text-[7px]"}`}>REVERSED</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
